import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, FormControl, Row, Spinner } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import MultiSelectField from '../common/MultiSelectField';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS, months } from '../../helpers/constants';
import { copyToClipboard, calculateCommissionDue, calculateRebate, downloadFileFromBase64, getCommissionInventoryUnit, getPnlValue, splitFullName } from '../../helpers/global';
import { ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import { debounce, throttle } from 'lodash';
import { checkboxActionCellTemplate } from '../common/PnLActionCell';
import { pnlLinkCellTemplate } from '../common/PnLLinkCell';
import { editableCellTemplate } from '../common/PnLEditableCell';
import { InventoryEditSideBar } from '../pipeline/Pipeline';
import DochubSidebar from '../pipeline/DochubSidebar';
import UnderlineButton from '../common/UnderlineButton';
import { Clipboard } from 'react-bootstrap-icons';
import { ContactEditSideBar } from '../crm/SearchCrm';
import ContractEditSideBar from '../main/ContractEditSidebar';

const giveInvoiceCellType = item => {
  if (item?.newStatus === 'Contracted') {
    if (item?.inventoryUnit) {
      return 'editable';
    }
    return 'text';
  }

  if (item?.inventoryUnit?.length) {
    return 'editable';
  }

  return 'text';
};

const getYears = () => {
  const nowYear = new Date().getFullYear();
  const years = [nowYear];

  for (let index = 1; index <= 5; index++) {
    years.push(nowYear - index);
  }

  return years.reverse();
};

const expenseFields = [
  { isPnLOption: true, key: 'Doc Prep' },
  { isPnLOption: true, key: 'Freight Reimbursement' },
  { isPnLOption: true, key: 'NPS' },
  { key: 'pnlRebate', valueGetter: calculateRebate },
  { key: 'SP/Com', valueGetter: c => Number(c.salesPrice || 0) - 499 }
];

const incomeFields = [
  {
    label: 'Invoice',
    key: 'invoice',
    valueGetter: c => (c.newStatus === 'Working' ? c?.inventoryUnit[0]?.invoice : c?.inventoryUnit?.invoice)
  },
  { label: 'Del & set', isPnLOption: true, key: 'Delivery/Set Up' },
  { label: 'Air Condition', isPnLOption: true, key: 'Air Condition' },
  { label: 'Pad', isPnLOption: true, key: 'Skirting' },
  { label: 'Concessions', isPnLOption: true, key: 'Utilities' },
  { label: 'Commission Due', key: 'Commission Due', valueGetter: calculateCommissionDue },
  { label: 'Team Leader', isPnLOption: true, key: 'Team Leader' },
  { label: 'Admin', isPnLOption: true, key: 'Admin' },
  { label: 'Service', isPnLOption: true, key: 'Service' },
  { label: 'Bank fees', isPnLOption: true, key: 'Bank Fees' }
];

const pnlHeaders = [
  {
    label: 'Buyer',
    key: 'buyer',
    valueGetter: c => {
      return splitFullName(c?.buyer);
    }
  },
  { label: 'Salesperson', key: 'salesperson' },
  { label: 'Status', key: 'newStatus' },
  { label: 'Funding status', key: 'fundingStatus' },
  {
    label: 'Size Overall',
    key: 'sizeOverall',
    valueGetter: c => (c.newStatus === 'Working' ? c?.inventoryUnit[0]?.sizeOverall : c?.inventoryUnit?.sizeOverall)
  },
  {
    label: 'Invoice',
    key: 'invoice',
    valueGetter: c => (c.newStatus === 'Working' ? c?.inventoryUnit[0]?.invoice : c?.inventoryUnit?.invoice)
  },
  { label: 'Del & set', isPnLOption: true, key: 'Delivery/Set Up' },
  { label: 'Air Condition', isPnLOption: true, key: 'Air Condition' },
  { label: 'Pad', isPnLOption: true, key: 'Skirting' },
  { label: 'Concessions', isPnLOption: true, key: 'Utilities' },
  { label: 'Commission Due', key: 'Commission Due', valueGetter: calculateCommissionDue },
  { label: 'Team Leader', isPnLOption: true, key: 'Team Leader' },
  { label: 'Admin', isPnLOption: true, key: 'Admin' },
  { label: 'Service', isPnLOption: true, key: 'Service' },
  { label: 'Bank fees', isPnLOption: true, key: 'Bank Fees' },

  { label: 'SP/Comm', key: 'SP/Com', valueGetter: c => Number(c.salesPrice || 0) - 499 },
  { label: 'Rebate', key: 'pnlRebate', valueGetter: calculateRebate, isEditable: true },
  { label: 'Frg. Reim', isPnLOption: true, key: 'Freight Reimbursement' },
  { label: 'NPS', isPnLOption: true, key: 'NPS' },
  { label: 'Doc Prep', isPnLOption: true, key: 'Doc Prep' },
  { label: 'TOTAL GP', key: 'TOTAL GP' },
  { label: 'Bank', key: 'lender', valueGetter: c => c?.lender?.shortName || '-' },
  {
    label: 'Factory',
    key: 'tradeInManufacturer',
    valueGetter: c =>
      c.newStatus === 'Contracted'
        ? c?.inventoryUnit?.manufacturer?.name
        : c?.inventoryUnit?.[0]?.manufacturer?.name || '-'
  },
  {
    label: 'Date of Deposit',
    key: 'dateOfDeposit',
    valueGetter: c => moment(c?.dateOfDeposit).format('MMMM Do YYYY')
  }
];

const defaultWidths = {
  action: 120,
  ...Object.fromEntries(
    pnlHeaders.map(header =>
      header?.label === 'Commission Due' || header?.label === 'Invoice' || header?.label === 'Buyer'
        ? [header.label, 200]
        : [header.label, 150]
    )
  )
};

const calculateTotalGP = (contract, appChoices) => {
  const reduceTotalValue = (prev, { key, valueGetter, isPnLOption }) => {
    let value;
    if (isPnLOption) {
      value = getPnlValue(contract, key, appChoices);
    } else {
      value = (valueGetter ? valueGetter(contract) : contract[key]) || 0;
    }

    return prev + Number(value);
  };

  const totalGPMinuend = expenseFields.reduce(reduceTotalValue, 0);
  const totalGPSubtrahend = incomeFields.reduce(reduceTotalValue, 0);

  return totalGPMinuend - totalGPSubtrahend;
};

const groupHeaderCellTemplate = {
  getCompatibleCell(uncertainCell) {
    return { ...uncertainCell };
  },
  update(cell, cellToMerge) {
    return { ...cell, ...cellToMerge };
  },
  render(cell) {
    return (
      <div className={cell.className} style={{ ...cell.style, width: '100%', height: '100%', gap: '8px' }} onClick={cell.onClick}>
        <span>{cell.text || ''}</span>
        {cell.onCopyClick && (
          <Clipboard
            className="hover-light cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              cell.onCopyClick();
            }}
            onPointerDown={e => e.stopPropagation()}
            size={14}
          />
        )}
      </div>
    );
  }
};

const getSalesLocationFilter = selectedIds => {
  if (!selectedIds.length) return {};
  if (selectedIds.length === 1) return { salesLocation: selectedIds[0] };
  return { salesLocation: { $in: selectedIds } };
};

const getContractSalesLocationId = contract => {
  const salesLocation = contract?.salesLocation;
  return salesLocation?._id != null ? String(salesLocation._id) : salesLocation != null ? String(salesLocation) : '';
};

// Per-location TOTAL GP / AVERAGE GP for CSV export and print (matches on-screen grouped summaries).
const getGpTotals = (contracts, appChoices) => {
  const totalGP = contracts.reduce((prev, c) => prev + calculateTotalGP(c, appChoices), 0);
  return { totalGP, averageGP: contracts.length ? totalGP / contracts.length : 0 };
};

const buildCopyGpSummaryRows = (contracts, appChoices) => {
  const { totalGP, averageGP } = getGpTotals(contracts, appChoices);
  const makeRow = (label, value) =>
    pnlHeaders
      .map((h, i) =>
        h.label === 'TOTAL GP'
          ? value.toFixed(2)
          : pnlHeaders[i + 1]?.label === 'TOTAL GP'
            ? label
            : ''
      )
      .join('\t');
  return [makeRow('Total Gp', totalGP), makeRow('AVERAGE GP', averageGP)];
};

const buildPrintGpSummaryRows = (contracts, appChoices) => {
  const { totalGP, averageGP } = getGpTotals(contracts, appChoices);
  const makeRow = (label, value) =>
    pnlHeaders.map((h, i) =>
      h.label !== 'TOTAL GP'
        ? pnlHeaders[i + 1]?.label === 'TOTAL GP'
          ? label
          : ''
        : `$${value.toFixed(2)}`
    );
  return [makeRow('TOTAL GP', totalGP), makeRow('AVERAGE GP', averageGP)];
};

const emptyPrintSpacerRow = () => pnlHeaders.map(() => '');

const exportColumnHeaderRow = () => pnlHeaders.map(h => h.label);

const buildCsvGpSummaryRow = (label, value) =>
  pnlHeaders.map((h, i) =>
    h.label === 'TOTAL GP' ? value.toFixed(2) : pnlHeaders[i + 1]?.label === 'TOTAL GP' ? label : ''
  );

// Plain-text exports (PDF via Sheets, CSV) cannot set cell styles — use heavier Unicode glyphs instead.
const toExportEmphasizedLocation = text =>
  String(text)
    .toUpperCase()
    .replace(/[A-Z0-9]/g, ch => {
      const code = ch.charCodeAt(0);
      if (code >= 65 && code <= 90) return String.fromCodePoint(0x1d5d4 + (code - 65));
      if (code >= 48 && code <= 57) return String.fromCodePoint(0x1d7ec + (code - 48));
      return ch;
    })
    .replace(/ /g, '\u3000');

const SelectionBox = ({
  excludedContracts,
  selectedMonth,
  onMonthChange,
  selectedYear,
  onYearChange,
  years,
  salesLocations,
  selectedSalesLocations,
  onSalesLocationChange,
  headers,
  filteredContracts,
  printDocument,
  printing,
  appChoices,
  searchQuery,
  setSearchQuery
}) => {
  const dataForCsv = useMemo(() => {
    const visible = filteredContracts.filter(c => !excludedContracts.includes(c['_id']));
    const data = [];

    // One block per selected location: location name, column headers, data rows, then Total GP / Average GP.
    // No top-level header row — headers sit under each location (array rows so CSVLink does not prepend one).
    selectedSalesLocations.forEach((locationId, index) => {
      const group = visible.filter(c => c.salesLocation?._id === locationId);
      if (!group.length) return;

      const locationName = salesLocations.find(s => s._id === locationId)?.location || 'Unknown';
      data.push([toExportEmphasizedLocation(locationName), ...pnlHeaders.slice(1).map(() => '')]);
      data.push(exportColumnHeaderRow());

      data.push(
        ...group.map(c =>
          headers.map(({ key, isPnLOption, valueGetter }) => {
            if (key === 'TOTAL GP') return calculateTotalGP(c, appChoices);
            return valueGetter ? valueGetter(c) : isPnLOption ? getPnlValue(c, key, appChoices) : c[key];
          })
        )
      );

      const { totalGP, averageGP } = getGpTotals(group, appChoices);
      data.push(buildCsvGpSummaryRow('Total Gp', totalGP));
      data.push(buildCsvGpSummaryRow('AVERAGE GP', averageGP));

      const hasNextGroup = selectedSalesLocations.slice(index + 1).some(id =>
        visible.some(c => c.salesLocation?._id === id)
      );
      if (hasNextGroup) data.push(emptyPrintSpacerRow());
    });

    return data;
  }, [headers, filteredContracts, appChoices, excludedContracts, selectedSalesLocations, salesLocations]);

  return (
    <Card className="border rounded">
      <Card.Header className="bg-primary">
        <h5 className="mb-0 text-white midFont">Dealboard (P&L)</h5>
      </Card.Header>
      <Card.Body className="p-1">
        <Row>
          {[
            {
              title: 'Search',
              type: 'text',
              value: searchQuery,
              onChange: setSearchQuery,
              hint: 'Search by buyer name...'
            },
            {
              title: 'Sales Locations',
              options: salesLocations.map(s => s.location),
              optionValues: salesLocations.map(s => s._id),
              value: selectedSalesLocations,
              onChange: onSalesLocationChange,
              multiple: true
            },
            { title: 'Select Month', options: ['', ...months], value: selectedMonth, onChange: onMonthChange },
            { title: 'Select Year', options: ['', ...years], value: selectedYear, onChange: onYearChange }
          ].map(({ title, options, optionValues, value, onChange, multiple, md = 3, type, hint }) => (
            <Col key={title} xs={6} md={md} className="px-4 mt-3">
              <h6 className="mb-1 smallFont">{title}</h6>
              {multiple ? (
                <MultiSelectField
                  options={options}
                  optionValues={optionValues}
                  value={value}
                  onChange={onChange}
                  hasSelectAll
                />
              ) : type === 'text' ? (
                <FormControl
                  value={value}
                  size="sm"
                  onChange={e => onChange(e.target.value)}
                  required
                  placeholder={hint}
                />
              ) : (
                <FormControl as="select" value={value} size="sm" onChange={e => onChange(e)} required>
                  {options.map((option, index) => (
                    <option key={option} value={optionValues ? optionValues[index] : option}>
                      {option}
                    </option>
                  ))}
                </FormControl>
              )}
            </Col>
          ))}
          <Col xs={12} className="mt-3 mb-1 text-right">
            <Button
              size="md"
              className="mx-2"
              onClick={printDocument}
              disabled={printing || filteredContracts.length === 0}
            >
              Print
            </Button>
            <CSVLink data={dataForCsv} filename="data.csv" className="btn btn-primary">
              Export as CSV
            </CSVLink>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const PnL = ({ appChoices }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [contacts, setContacts] = useState([]);

  const [lists, setLists] = useState([]);

  const [years] = useState(getYears());
  const [selectedYear, setSelectedYear] = useState(years[years.length - 1]);
  const [selectedMonth, setSelectedMonth] = useState(months[new Date().getMonth()]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [salesLocations] = useState(appChoices.find(ac => ac.key === 'salesLocation').values);
  const [selectedSalesLocations, setSelectedSalesLocations] = useState([salesLocations[0]._id]);

  const [printing, setPrinting] = useState(false);
  const [excludedContracts, setExcludedContracts] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const [selectedAreas, setSelectedAreas] = useState([]);

  const [editingInventory, setEditingInventory] = useState(null);

  const [openDochubMeta, setOpenDochubMeta] = useState(null);

  const [columnWidths, setColumnWidths] = useState(() => {
    const savedWidths = localStorage.getItem('pnl-column-widths');
    return savedWidths ? JSON.parse(savedWidths) : defaultWidths;
  });
  const [pendingCells, setPendingCells] = useState({});
  const [updateContactMetadata, setUpdateContactMetadata] = useState(null);

  const handleColumnResized = (columnId, newWidth) => {
    const clampedWidth = Math.max(80, Math.min(500, newWidth));
    setColumnWidths(prev => {
      const newWidths = {
        ...prev,
        [columnId]: clampedWidth
      };
      saveColumnWidths(newWidths);
      return newWidths;
    });
  };

  const onContractDelete = contractId => {
    const index = filteredContracts.findIndex(c => c['_id'] === contractId);
    if (index !== -1) {
      filteredContracts.splice(index, 1);
      setFilteredContracts([...filteredContracts]);
    }
    setUpdateContactMetadata(null);
  };

  const onContractFileLoaded = (crmId, fileNames) => {
    const index = filteredContracts.findIndex(c => c['_id'] === crmId);
    if (index !== -1) {
      filteredContracts[index]['fileNames'] = fileNames;
      setFilteredContracts([...filteredContracts]);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      setSelectedYear('');
      setSelectedMonth('');
    } else {
      setSelectedYear(years[years.length - 1]);
      setSelectedMonth(months[new Date().getMonth()]);
    }
  }, [searchQuery]);

  const onExcludeContractChange = (id, checked) => {
    if (checked) {
      const exisiting = excludedContracts.indexOf(id);
      if (exisiting !== -1) excludedContracts.splice(exisiting, 1);
    } else {
      if (!excludedContracts.includes(id)) excludedContracts.push(id);
    }
    setExcludedContracts([...excludedContracts]);
  };

  const printDocument = async () => {
    toast.info('Submitting report, please wait...');
    setPrinting(true);

    const visible = filteredContracts.filter(c => !excludedContracts.includes(c['_id']));
    const tableRows = [];

    // Same per-location grouping as the table and CSV export.
    selectedSalesLocations.forEach((locationId, index) => {
      const group = visible.filter(c => c.salesLocation?._id === locationId);
      if (!group.length) return;

      const locationName = salesLocations.find(s => s._id === locationId)?.location || 'Unknown';
      tableRows.push(pnlHeaders.map((h, i) => (i === 0 ? toExportEmphasizedLocation(locationName) : '')));
      tableRows.push(exportColumnHeaderRow());

      tableRows.push(
        ...group.map(c =>
          pnlHeaders.map(({ key, label, valueGetter, isPnLOption }) => {
            if (valueGetter) return valueGetter(c);
            if (label === 'TOTAL GP') return `$${calculateTotalGP(c, appChoices).toFixed(2)}`;

            return isPnLOption ? getPnlValue(c, key, appChoices) : c[key] ? c[key] : '-';
          })
        )
      );
      tableRows.push(...buildPrintGpSummaryRows(group, appChoices));

      const hasNextGroup = selectedSalesLocations.slice(index + 1).some(id =>
        visible.some(c => c.salesLocation?._id === id)
      );
      if (hasNextGroup) tableRows.push(emptyPrintSpacerRow());
    });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.DOCHUB_PRINT_TABLE,
      requestBody: {
        table: tableRows,
        fileName: `pnl-${new Date().toDateString()}.pdf`
      }
    });

    setPrinting(false);
    if (error) return toast.error(error);

    toast.success('Document created successfully, file is being downloaded...');
    const { fileName, data, mimeType } = response;
    downloadFileFromBase64({ fileName, data, mimeType });
  };

  const fetchContracts = async () => {
    setLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTRACTS_SEARCH,
      requestBody: {
        filter: {
          dateOfDeposit: { $ne: null },
          inventoryUnit: { $ne: null },
          ...getSalesLocationFilter(selectedSalesLocations)
        },
        sort: '-dateOfDeposit'
      }
    });

    setLoading(false);
    if (error) {
      return toast.error(error);
    }

    setContracts(response);
  };

  const fetchContacts = async () => {
    setLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_SEARCH,
      requestBody: {
        filter: {
          dateOfDeposit: { $ne: null },
          inventoryUnit: { $ne: [] },
          ...getSalesLocationFilter(selectedSalesLocations)
        },
        sort: '-dateOfDeposit'
      }
    });

    setLoading(false);
    if (error) {
      return toast.error(error);
    }

    setContacts(response);
  };

  useEffect(() => {
    // Empty selection = no data (do not fetch all locations).
    if (!selectedSalesLocations.length) {
      setContracts([]);
      setContacts([]);
      setLoading(false);
      return;
    }
    fetchContracts();
    fetchContacts();
  }, [selectedSalesLocations.join(',')]);

  useEffect(() => {
    const allItems = [
      ...contracts.map(c => ({ newStatus: 'Contracted', ...c })),
      ...contacts
        .filter((c, a) => {
          const isContactContracted = contracts.find(
            cO => cO._id === c?.exportedContract || cO._id === c?.exportedContract?._id
          );

          return !isContactContracted && c?.inventoryUnit?.length === 1;
        })
        .map(c => ({ newStatus: 'Working', ...c }))
    ].sort((a, b) => new Date(b.dateOfDeposit) - new Date(a.dateOfDeposit));
    setLists(allItems);
  }, [contacts, contracts]);

  const filterContracts = () => {
    if (!selectedSalesLocations.length) {
      setFilteredContracts([]);
      return;
    }

    const tempContracts = lists.filter(contract => {
      // Only include contracts for explicitly selected locations.
      if (!selectedSalesLocations.includes(contract?.salesLocation?._id)) return false;

      if (searchQuery && !contract?.buyer?.toLowerCase().includes(searchQuery.toLowerCase())) return false;

      const dateToFilter = new Date(contract?.dateOfDeposit);

      if (selectedMonth) {
        const month = months.indexOf(selectedMonth);
        if (month !== dateToFilter.getMonth()) return false;
      }

      if (selectedYear) {
        if (selectedYear !== dateToFilter.getFullYear()) return false;
      }

      return true;
    });

    setFilteredContracts([...tempContracts]);
  };

  const updateBuyerName = response => {
    if (updateContactMetadata?.newStatus === 'Contracted') {
      setContracts(
        contracts?.map(contract =>
          contract?._id === response?._id ? { ...response, newStatus: updateContactMetadata?.newStatus } : contract
        )
      );
    } else {
      setContacts(
        contacts?.map(contact =>
          contact?._id === response?._id ? { ...response, newStatus: updateContactMetadata?.newStatus } : contact
        )
      );
    }
    setUpdateContactMetadata(null);
  };

  const mergeInventoryIntoRecord = useCallback((record, updatedInventory) => {
    if (!record || !updatedInventory) return record;

    if (record.newStatus === 'Working') {
      return {
        ...record,
        inventoryUnit: [updatedInventory]
      };
    }

    return {
      ...record,
      inventoryUnit: updatedInventory
    };
  }, []);

  const updateInventoryUnit = useCallback(async (contract, field, value, key) => {
    const inventoryUnit = getCommissionInventoryUnit(contract);
    if (!inventoryUnit?._id) {
      toast.error('No inventory unit linked to this deal.');
      return;
    }

    const updateBody = {
      _id: inventoryUnit._id,
      [field]: value
    };

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventoryUnit._id),
      requestBody: updateBody,
      method: 'PUT'
    });

    if (error) return toast.error(error);

    const isContact = contract?.newStatus === 'Working';
    const mergedRecord = mergeInventoryIntoRecord(contract, response);

    if (isContact) {
      setContacts(prev => prev.map(c => (c._id === contract._id ? mergedRecord : c)));
    } else {
      setContracts(prev => prev.map(c => (c._id === contract._id ? mergedRecord : c)));
    }

    toast.success('Updated Successfully');
    setPendingCells(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  }, [mergeInventoryIntoRecord]);

  const debouncedUpdateInventoryUnit = useMemo(() => debounce(updateInventoryUnit, 500), [updateInventoryUnit]);

  const handleCellsChanged = changes => {
    if (changes.length !== 1) return;
    changes.forEach(change => {
      const { rowId, columnId, newCell } = change;

      if (columnId === 'print') {
        const isChecked = newCell.checked;
        const contractId = rowId.slice('data-'.length);
        if (contractId) onExcludeContractChange(contractId, isChecked);
        return;
      }

      if (rowId.startsWith('data-')) {
        const contractId = rowId.slice('data-'.length);
        const contract = filteredContracts.find(c => c._id === contractId);
        if (!contract) return;

        const header = pnlHeaders.find(h => h.label === columnId);
        if (!header) return;

        if (newCell.nonEditable || !(header.isEditable || header.isPnLOption)) return;

        const value = newCell.text;
        const numericValue = parseFloat(value);

        if (isNaN(numericValue)) {
          toast.error(`Invalid number for ${header.label}`);
          return;
        }

        if (header.isEditable) {
          const key = `${contract._id}-${header.key}`;
          setPendingCells(prev => ({ ...prev, [key]: true }));
          debouncedUpdateInventoryUnit(contract, header.key, numericValue, key);
        } else if (header.isPnLOption) {
          const inventoryUnit = getCommissionInventoryUnit(contract);
          const currentOptions = inventoryUnit?.pnlOptions || [];
          const existingIndex = currentOptions.findIndex(o => o.key === header.key);
          const newOptions = [...currentOptions];

          if (existingIndex !== -1) {
            newOptions[existingIndex].value = numericValue;
          } else {
            newOptions.push({ key: header.key, value: numericValue });
          }
          const key = `${contract._id}-${header.key}`;
          setPendingCells(prev => ({ ...prev, [key]: true }));

          debouncedUpdateInventoryUnit(contract, 'pnlOptions', newOptions, key);
        }
      }
    });
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    filterContracts();
  }, [lists, selectedYear, selectedMonth, selectedSalesLocations.join(','), searchQuery]);

  const onYearChange = e => {
    setSelectedYear(e.target.value ? Number(e.target.value) : null);
  };

  const onMonthChange = e => {
    setSelectedMonth(e.target.value);
  };

  const onSalesLocationChange = ids => {
    setSelectedSalesLocations(ids);
  };

  const toggleGroupCollapse = locationId => {
    setCollapsedGroups(prev => ({
      ...prev,
      [locationId]: !prev[locationId]
    }));
  };

  const formatContractRowForCopy = contract =>
    pnlHeaders
      .map(header => {
        let value;
        if (header.label === 'TOTAL GP') {
          value = calculateTotalGP(contract, appChoices);
        } else if (header.valueGetter) {
          value = header.valueGetter(contract);
        } else if (header.isPnLOption) {
          value = getPnlValue(contract, header.key, appChoices);
        } else {
          value = contract[header.key];
        }

        return typeof value === 'number' ? value.toFixed(2) : value || '-';
      })
      .join('\t');

  const handleCopyRow = contract => {
    const values = pnlHeaders.map(header => {
      let value;

      if (header.label === 'TOTAL GP') {
        value = calculateTotalGP(contract, appChoices);
      } else if (header.valueGetter) {
        value = header.valueGetter(contract);
      } else if (header.isPnLOption) {
        value = getPnlValue(contract, header.key, appChoices);
      } else {
        value = contract[header.key];
      }

      if (typeof value === 'number') {
        value = value.toFixed(2);
      }

      if (!value) {
        value = '-';
      }

      return value;
    });

    const tsvLine = values.join('\t');
    copyToClipboard(tsvLine);
    toast.success('Row copied to clipboard');
  };

  const handleCopyLocationRows = useCallback(
    locationId => {
      const visible = filteredContracts.filter(c => !excludedContracts.includes(c['_id']));
      const group = visible.filter(c => getContractSalesLocationId(c) === String(locationId));

      if (!group.length) {
        toast.error('No rows to copy');
        return;
      }

      const locationName =
        salesLocations.find(s => String(s._id) === String(locationId))?.location || 'Unknown';
      const lines = [pnlHeaders.map((h, i) => (i === 0 ? locationName : '')).join('\t')];

      group.forEach(contract => {
        lines.push(formatContractRowForCopy(contract));
      });
      buildCopyGpSummaryRows(group, appChoices).forEach(row => lines.push(row));

      copyToClipboard(lines.join('\n'));
      toast.success(`${locationName} rows copied to clipboard`);
    },
    [filteredContracts, appChoices, excludedContracts, salesLocations]
  );

  const columns = useMemo(
    () => [
      {
        columnId: 'action',
        width: columnWidths['action'],
        resizable: true,
        headerCell: () => 'Action'
      },
      ...pnlHeaders.map(header => ({
        columnId: header.label,
        width: columnWidths[header.label],
        resizable: true,
        headerCell: () => header.label
      }))
    ],
    [columnWidths]
  );

  const getRows = () => {
    const headerRow = {
      rowId: 'header',
      height: 60,
      cells: [
        {
          type: 'header',
          text: 'Action',
          className: 'bg-dark text-light text-center d-flex justify-content-center',
          style: { fontSize: '12px', fontWeight: 'bold' }
        },
        ...pnlHeaders.map(h => ({
          type: 'header',
          text: h.label,
          className: 'bg-dark text-light d-flex justify-content-center',
          style: { fontSize: '12px', fontWeight: 'bold' }
        }))
      ]
    };

    const commonCellStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: 'semibold'
    };

    const buildDataRow = contract => ({
      rowId: `data-${contract._id}`,
      height: 60,
      cells: [
        {
          type: 'checkboxAction',
          checked: !excludedContracts.includes(contract._id),
          onCheckboxChange: checked => onExcludeContractChange(contract._id, checked),
          onCopyClick: () => handleCopyRow(contract),
          style: commonCellStyle
        },
        ...pnlHeaders.map(header => {
          const { label, key, isPnLOption, valueGetter, isEditable } = header;

          const pendingKey = `${contract._id}-${header.key}`;
          const isPending = pendingCells[pendingKey];

          if (label === 'TOTAL GP') {
            return {
              type: 'text',
              text: `$${calculateTotalGP(contract, appChoices).toFixed(2)}`,
              className: 'font-weight-bold bg-light',
              nonEditable: true,
              style: commonCellStyle
            };
          }

          let value = isPnLOption ? getPnlValue(contract, key, appChoices) : contract[key];
          if (valueGetter) value = valueGetter(contract);
          const displayValue = typeof value === 'number' ? value.toFixed(2) : value || '-';

          if (key === 'buyer') {
            return {
              type: 'editable',
              text: displayValue,
              _id: contract._id,
              newStatus: contract.newStatus,
              nonEditable: true,
              onClick: () => {
                setUpdateContactMetadata(contract);
              },
              style: {
                ...commonCellStyle,
                backgroundColor: '#F8F9FA'
              }
            };
          }

          if (key === 'invoice') {
            return {
              type: giveInvoiceCellType(contract),
              text: displayValue,
              nonEditable: true,
              onClick: () => {
                setEditingInventory({
                  unit: contract?.newStatus === 'Contracted' ? contract?.inventoryUnit : contract?.inventoryUnit[0],
                  isContract: contract?.newStatus === 'Contracted'
                });
              },
              style: {
                ...commonCellStyle,
                backgroundColor: '#F8F9FA'
              }
            };
          }

          if (key === 'Commission Due') {
            return {
              type: 'editable',
              text: displayValue,
              nonEditable: true,
              onClick: () => {
                setOpenDochubMeta({
                  contractId: contract?.newStatus === 'Contracted' ? contract?._id : null,
                  contactId: contract?.newStatus === 'Working' ? contract?._id : null
                });
              },
              style: {
                ...commonCellStyle,
                backgroundColor: '#F8F9FA'
              }
            };
          }

          return {
            type: 'text',
            text: displayValue,
            nonEditable: !isPnLOption && !isEditable,
            className: !isPnLOption && !isEditable ? 'bg-light' : '',
            style: {
              ...commonCellStyle,
              opacity: isPending ? 0.5 : 1,
              backgroundColor: isPending ? '#fff3cd' : 'inherit'
            }
          };
        })
      ]
    });

    const buildGroupHeaderRow = (locationId, locationName, isCollapsed) => ({
      rowId: `group-${locationId}`,
      locationName,
      height: 36,
      cells: [
        {
          type: 'groupHeader',
          text: isCollapsed ? '▶' : '▼',
          nonEditable: true,
          onClick: () => toggleGroupCollapse(locationId),
          className: 'font-weight-bold user-select-none',
          style: { ...commonCellStyle, cursor: 'pointer' }
        },
        {
          type: 'groupHeader',
          text: locationName,
          nonEditable: true,
          onClick: () => toggleGroupCollapse(locationId),
          onCopyClick:
            selectedSalesLocations.length > 1 ? () => handleCopyLocationRows(locationId) : undefined,
          className: 'font-weight-bold',
          style: { ...commonCellStyle, cursor: 'pointer', justifyContent: 'flex-start' }
        },
        ...pnlHeaders.slice(1).map(() => ({
          type: 'groupHeader',
          text: '',
          nonEditable: true
        }))
      ]
    });

    if (!filteredContracts?.length) {
      return [headerRow];
    }

    const totalGPColIndex = pnlHeaders.findIndex(h => h.label === 'TOTAL GP') + 1;

    const locationOrder = selectedSalesLocations;

    const groupedRows = locationOrder.flatMap(locationId => {
      const locationContracts = filteredContracts.filter(c => c.salesLocation?._id === locationId);
      if (!locationContracts.length) return [];

      const locationName = salesLocations.find(s => s._id === locationId)?.location || 'Unknown';
      const isCollapsed = collapsedGroups[locationId];

      const summaryRows = isCollapsed
        ? []
        : (() => {
          const totalGP = locationContracts.reduce((prev, c) => prev + calculateTotalGP(c, appChoices), 0);
          const averageGP = locationContracts.length > 0 ? totalGP / locationContracts.length : 0;

          const totalRow = {
            rowId: `${locationId}-total-gp`,
            height: 40,
            cells: [
              {
                type: 'text',
                text: '',
                nonEditable: true,
                className: 'border-right-0 user-select-none'
              },

              ...Array(totalGPColIndex - 2).fill({
                type: 'text',
                text: '',
                nonEditable: true,
                className: 'border-right-0'
              }),

              {
                type: 'text',
                text: 'TOTAL GP',
                className: 'text-end fw-bold ',
                nonEditable: true,
                style: commonCellStyle
              },

              {
                type: 'text',
                text: `$${totalGP.toFixed(2)}`,
                className: 'fw-bold ',
                nonEditable: true,
                style: commonCellStyle
              }
            ]
          };

          const averageRow = {
            rowId: `${locationId}-average-gp`,
            height: 40,
            cells: [
              {
                type: 'text',
                text: '',
                nonEditable: true,
                className: 'border-right-0 user-select-none'
              },

              ...Array(totalGPColIndex - 2).fill({
                type: 'text',
                text: '',
                nonEditable: true,
                className: 'border-right-0 user-select-none'
              }),

              {
                type: 'text',
                text: 'AVERAGE GP',
                className: 'text-end fw-bold',
                nonEditable: true,
                style: commonCellStyle
              },

              {
                type: 'text',
                text: `$${averageGP.toFixed(2)}`,
                className: 'fw-bold',
                nonEditable: true,
                style: commonCellStyle
              }
            ]
          };

          return [totalRow, averageRow];
        })();
      return [
        buildGroupHeaderRow(locationId, locationName, isCollapsed),
        ...(isCollapsed ? [] : locationContracts.map(buildDataRow)),
        ...summaryRows
      ];
    });
    return [headerRow, ...groupedRows];
  };

  const rows = getRows();

  useEffect(() => {
    const getLocationNameForRow = rowIndex => {
      for (let idx = rowIndex; idx >= 0; idx--) {
        const candidate = rows[idx];
        if (String(candidate?.rowId || '').startsWith('group-')) {
          return candidate.locationName || candidate.cells?.[1]?.text || '';
        }
      }
      return '';
    };

    const getSelectedText = () =>
      selectedAreas
        .flatMap(area => {
          const lines = [];
          const emittedLocations = new Set();
          for (let rowIdx = area.top; rowIdx <= area.bottom; rowIdx++) {
            const row = rows[rowIdx];
            if (!row || row.rowId === 'header') continue;

            // Location group headers are one logical cell for the whole row.
            // Always copy the location name even if its column is outside the selection.
            if (String(row.rowId || '').startsWith('group-')) {
              const locationName = row.locationName || row.cells[1]?.text || '';
              lines.push(locationName);
              if (locationName) emittedLocations.add(locationName);
              continue;
            }

            const rowLocation = getLocationNameForRow(rowIdx);
            if (rowLocation && !emittedLocations.has(rowLocation)) {
              lines.push(rowLocation);
              emittedLocations.add(rowLocation);
            }

            const rowCells = [];
            for (let colIdx = area.left; colIdx <= area.right; colIdx++) {
              rowCells.push(row.cells[colIdx]?.text || '');
            }
            lines.push(rowCells.join('\t'));
          }
          return lines;
        })
        .join('\n');

    const handleCopy = e => {
      if (!selectedAreas?.length) return;
      const text = getSelectedText();
      if (!text) return;

      if (e.clipboardData?.setData) {
        e.preventDefault();
        e.clipboardData.setData('text/plain', text);
        return;
      }

      // Fallback path for environments without clipboardData on the event.
      e.preventDefault();
      copyToClipboard(text);
    };

    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, [selectedAreas, rows]);

  const saveColumnWidths = useCallback(
    throttle(widths => {
      localStorage.setItem('pnl-column-widths', JSON.stringify(widths));
    }, 1000),
    []
  );

  const handleCopyAll = useCallback(() => {
    const visible = filteredContracts.filter(c => !excludedContracts.includes(c['_id']));
    if (visible.length === 0) {
      toast.error('No rows to copy');
      return;
    }

    const lines = [pnlHeaders.map(h => h.label).join('\t')];

    // Same per-location grouping as CSV export and print.
    selectedSalesLocations.forEach((locationId, index) => {
      const group = visible.filter(c => getContractSalesLocationId(c) === String(locationId));
      if (!group.length) return;

      const locationName =
        salesLocations.find(s => String(s._id) === String(locationId))?.location || 'Unknown';
      lines.push(pnlHeaders.map((h, i) => (i === 0 ? locationName : '')).join('\t'));

      group.forEach(contract => {
        lines.push(formatContractRowForCopy(contract));
      });

      buildCopyGpSummaryRows(group, appChoices).forEach(row => lines.push(row));

      const hasNextGroup = selectedSalesLocations.slice(index + 1).some(id =>
        visible.some(c => getContractSalesLocationId(c) === String(id))
      );
      if (hasNextGroup) lines.push(emptyPrintSpacerRow().join('\t'));
    });

    copyToClipboard(lines.join('\n'));
    toast.success('All rows copied to clipboard');
  }, [filteredContracts, appChoices, excludedContracts, selectedSalesLocations, salesLocations]);

  useEffect(() => {
    const savedWidths = localStorage.getItem('pnl-column-widths');
    if (savedWidths) {
      try {
        const parsed = JSON.parse(savedWidths);
        setColumnWidths(prev => ({
          ...defaultWidths,
          ...parsed
        }));
      } catch (e) {
        console.error('Error parsing saved column widths:', e);
      }
    }
  }, [defaultWidths]);

  return (
    <div className="px-1 py-4">
      <SelectionBox
        years={years}
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        selectedYear={selectedYear}
        onYearChange={onYearChange}
        salesLocations={salesLocations}
        selectedSalesLocations={selectedSalesLocations}
        onSalesLocationChange={onSalesLocationChange}
        printing={printing}
        filteredContracts={filteredContracts}
        printDocument={printDocument}
        headers={pnlHeaders}
        appChoices={appChoices}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        excludedContracts={excludedContracts}
      />
      <hr />
      <div className="d-flex align-items-center gap-2 mb-2">
        <h6 className="mb-0">P&L:</h6>
        {filteredContracts?.length > 0 ? (
          <UnderlineButton
            onClick={handleCopyAll}
            variant="success"
            text="Copy all rows"
            disabled={filteredContracts.length === 0}
            Icon={Clipboard}
            iconSize={14}
          />
        ) : null}
      </div>

      <div
        className="overflow-auto hide-scrollbar"
        style={{
          maxHeight: filteredContracts?.length ? 'calc(100vh - 100px)' : 'auto'
        }}
      >
        <ReactGrid
          rows={rows}
          columns={columns}
          onCellsChanged={handleCellsChanged}
          onColumnResized={handleColumnResized}
          enableRangeSelection
          enableColumnSelection
          stickyTopRows={1}
          stickyLeftColumns={2}
          horizontalStickyBreakpoint={100}
          selectedAreas={selectedAreas}
          onSelectionChanged={setSelectedAreas}
          customCellTemplates={{
            checkboxAction: checkboxActionCellTemplate,
            link: pnlLinkCellTemplate,
            editable: editableCellTemplate,
            groupHeader: groupHeaderCellTemplate
          }}
          style={{ minWidth: `${Object.values(columnWidths).reduce((a, b) => a + b, 0)}px` }}
        />
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none',
              zIndex: 1000
            }}
          >
            <Spinner animation="border" />
          </div>
        )}
      </div>

      {!filteredContracts?.length ? <h6 className="my-2 text-center">No contracts to show</h6> : null}
      <h6 className="text-right text-muted mt-2">
        Total Reports: <span className="text-dark">{filteredContracts.length}</span>
      </h6>

      {(openDochubMeta?.contractId || openDochubMeta?.contactId) && (
        <DochubSidebar
          show={openDochubMeta}
          onHide={() => setOpenDochubMeta(null)}
          appChoices={appChoices}
          source={openDochubMeta?.contractId ? 'Contract' : 'Contact'}
          contractId={openDochubMeta?.contractId}
          contactId={openDochubMeta?.contactId}
          onUpdateDocHub={(updatedResponse, isPrintOperation = false) => {
            if (openDochubMeta.contractId) {
              setContracts(prev => prev.map(c => (c._id === updatedResponse._id ? updatedResponse : c)));
            } else {
              setContacts(prev => prev.map(c => (c._id === updatedResponse._id ? updatedResponse : c)));
            }

            if (!isPrintOperation) {
              setOpenDochubMeta(null);
            }
          }}
        />
      )}

      <InventoryEditSideBar
        appChoices={appChoices}
        onHide={() => setEditingInventory(null)}
        editingInventory={editingInventory?.unit}
        onInventoryEdit={updatedInventory => {
          if (editingInventory?.isContract) {
            setContracts(prevContracts =>
              prevContracts.map(contract => {
                if (contract.inventoryUnit?._id === updatedInventory._id) {
                  return { ...contract, inventoryUnit: updatedInventory };
                }
                return contract;
              })
            );
          } else {
            setContacts(prevContacts =>
              prevContacts.map(contact => {
                if (contact.inventoryUnit[0]?._id === updatedInventory._id) {
                  return { ...contact, inventoryUnit: [updatedInventory] };
                }
                return contact;
              })
            );
          }
          setEditingInventory(null);
        }}
        onInventoryDelete={deletedInventoryId => {
          if (editingInventory?.isContract) {
            setContracts(prevContracts =>
              prevContracts.map(contract => {
                if (contract.inventoryUnit?._id === deletedInventoryId) {
                  return { ...contract, inventoryUnit: null };
                }
                return contract;
              })
            );
          } else {
            setContacts(prevContacts =>
              prevContacts.map(contact => {
                if (contact.inventoryUnit?._id === deletedInventoryId) {
                  return { ...contact, inventoryUnit: null };
                }
                return contact;
              })
            );
          }

          setEditingInventory(null);
        }}
      />

      <ContactEditSideBar
        editingContact={updateContactMetadata?.newStatus === 'Working' ? updateContactMetadata : null}
        appChoices={appChoices}
        onContactEdit={updateBuyerName}
        onContactDelete={onContractDelete}
        onCRMFileLoaded={onContractFileLoaded}
        onContactEditSideBarHide={() => setUpdateContactMetadata(null)}
      />

      <ContractEditSideBar
        editingContract={updateContactMetadata?.newStatus === 'Contracted' ? updateContactMetadata : null}
        appChoices={appChoices}
        onContractEdit={updateBuyerName}
        onContractDelete={onContractDelete}
        onContractFileLoaded={onContractFileLoaded}
        onHide={() => setUpdateContactMetadata(null)}
      />
    </div>
  );
};

export default PnL;
